/**
 * MessageContent — renders parsed markdown content in assistant chat bubbles.
 * CodeBlockView — standalone code block with copy-to-clipboard.
 *
 * Extracted from TestOpsHub.tsx (lines ~714–945).
 */

import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import clsx from 'clsx'
import type { InlineContext } from '../types'
import { parseRichText, renderInline } from '../utils/markdown'

// ---------------------------------------------------------------------------
// CodeBlockView
// ---------------------------------------------------------------------------

export function CodeBlockView({
  language,
  lines,
  marginTop,
}: {
  language: string | null
  lines: string[]
  marginTop: boolean
}) {
  const [copied, setCopied] = useState(false)
  const codeText = lines.join('\n')

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeText)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access denied or unsupported (e.g. http on a non-localhost
      // origin) — fail silently rather than spamming the user.
    }
  }

  return (
    <div
      className={clsx(
        'overflow-hidden rounded-lg border border-subtle bg-card',
        marginTop && 'mt-1'
      )}
    >
      <div className="flex items-center justify-between border-b border-subtle bg-foreground/[0.04] px-3 py-1">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {language || 'text'}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground transition hover:bg-foreground/[0.10] hover:text-foreground"
          aria-label={copied ? 'Copied to clipboard' : 'Copy code to clipboard'}
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto px-3 py-2">
        <code className="block whitespace-pre font-mono text-[12px] leading-5 text-foreground">
          {codeText}
        </code>
      </pre>
    </div>
  )
}

// ---------------------------------------------------------------------------
// MessageContent
// ---------------------------------------------------------------------------

export function MessageContent({
  content,
  messageId,
  citableCallIds,
}: {
  content: string
  messageId?: string
  citableCallIds?: Set<string>
}) {
  const blocks = parseRichText(content)
  const ctx: InlineContext = { messageId, citableCallIds }

  return (
    <div className="space-y-3 text-sm leading-7 text-foreground">
      {blocks.map((block, index) => {
        const previousBlock = index > 0 ? blocks[index - 1] : null

        if (block.type === 'label') {
          return (
            <div
              key={`label-${index}`}
              className={clsx(
                'text-sm font-semibold tracking-tight text-foreground',
                previousBlock ? 'pt-3' : 'pt-1'
              )}
            >
              {renderInline(block.content, ctx)}
            </div>
          )
        }

        if (block.type === 'paragraph') {
          const joined = block.lines.join(' ')
          return (
            <p
              key={`paragraph-${index}`}
              className={clsx(
                'whitespace-pre-wrap',
                previousBlock?.type === 'label' && 'mt-1'
              )}
            >
              {renderInline(joined, ctx)}
            </p>
          )
        }

        if (block.type === 'heading') {
          // ATX-style markdown heading. Sizes scale by level so the
          // visual hierarchy is obvious in the chat bubble.
          const sizeClass =
            block.level === 1
              ? 'text-xl font-semibold'
              : block.level === 2
                ? 'text-lg font-semibold'
                : block.level === 3
                  ? 'text-base font-semibold'
                  : 'text-sm font-semibold'
          const Tag = `h${block.level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
          return (
            <Tag
              key={`heading-${index}`}
              className={clsx(
                sizeClass,
                'tracking-tight text-foreground',
                index > 0 && 'pt-2'
              )}
            >
              {renderInline(block.content, ctx)}
            </Tag>
          )
        }

        if (block.type === 'blockquote') {
          return (
            <blockquote
              key={`quote-${index}`}
              className={clsx(
                'border-l-4 border-accent/40 bg-accent-soft/30 px-3 py-1 text-foreground italic',
                previousBlock?.type === 'label' && 'mt-1'
              )}
            >
              {block.lines.map((line, lineIndex) => (
                <p key={`quote-line-${lineIndex}`} className="leading-relaxed">
                  {renderInline(line, ctx)}
                </p>
              ))}
            </blockquote>
          )
        }

        if (block.type === 'code-block') {
          return (
            <CodeBlockView
              key={`code-${index}`}
              language={block.language}
              lines={block.lines}
              marginTop={previousBlock?.type === 'label'}
            />
          )
        }

        if (block.type === 'bullet-list') {
          return (
            <ul
              key={`bullet-${index}`}
              className={clsx(
                'list-disc space-y-1 pl-5',
                previousBlock?.type === 'label' && 'mt-1'
              )}
            >
              {block.items.map((item, itemIndex) => (
                <li key={`bullet-item-${itemIndex}`}>{renderInline(item, ctx)}</li>
              ))}
            </ul>
          )
        }

        if (block.type === 'numbered-list') {
          return (
            <ol
              key={`numbered-${index}`}
              className={clsx(
                'list-decimal space-y-1 pl-5',
                previousBlock?.type === 'label' && 'mt-1'
              )}
            >
              {block.items.map((item, itemIndex) => (
                <li key={`numbered-item-${itemIndex}`}>{renderInline(item, ctx)}</li>
              ))}
            </ol>
          )
        }

        // Markdown table — wrap in overflow-x-auto so wide tables (like Jira
        // issue lists with long URLs) scroll horizontally on narrow chats.
        return (
          <div
            key={`table-${index}`}
            className={clsx(
              'overflow-x-auto',
              previousBlock?.type === 'label' && 'mt-1'
            )}
          >
            <table className="min-w-full divide-y divide-subtle overflow-hidden rounded-lg border border-subtle text-sm">
              {block.headers.length > 0 && (
                <thead className="bg-foreground/[0.04]">
                  <tr>
                    {block.headers.map((header, headerIndex) => (
                      <th
                        key={`th-${headerIndex}`}
                        className="whitespace-nowrap px-3 py-2 text-left font-semibold text-foreground"
                      >
                        {renderInline(header, ctx)}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody className="divide-y divide-subtle bg-card">
                {block.rows.map((row, rowIndex) => (
                  <tr key={`tr-${rowIndex}`} className="hover:bg-foreground/[0.04]">
                    {row.map((cell, cellIndex) => (
                      <td
                        key={`td-${rowIndex}-${cellIndex}`}
                        className="px-3 py-2 align-top text-foreground"
                      >
                        {renderInline(cell, ctx)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      })}
    </div>
  )
}
